'use client';

import useIsMobile from '@/hooks/useIsMobile';
import { eventTracker } from '@/lib/events/cjrEventTracker';
import { AppOtplessConfig, CohortModel } from '@/types/types';
import { cn } from '@admin/lib/utils';
import { Dialog } from '@headlessui/react';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAllCohorts } from '../../RevampedLanding/action';
import BookDemoStep from './BookDemoStep';
import GoalMarketingLoadingSkeleton from './GoalMarketing/GoalMarketingLoadingSkeleton';
import SelectGoalStep from './SelectGoalStep';
import Stepper from './Stepper';

import Loader from '@/components/ui/loader';
import { useFirebaseRemoteConfig } from '@/contexts/Firebase/firebase-remote-config.context';
import { OTPLessProvider, TRUECALLER_RES } from '@/contexts/otpless.context';
import { useDeviceDetails } from '@/hooks/useDeviceDetails';
import { APP_METHODS, APP_REDIRECTION_SCREENS } from '@/utils/enums';
import { onOneTapOtpless } from '@/utils/otpless-actions/one-tap';
import { getCurrEnvOtplessConfig, invokeAppMethod, isFromApp } from '@/utils/utils';
import { verifyReferralToken } from '@/widgets/action';
import { GoalProps } from '@/widgets/landing-page-widgets/hero-section/schema';
import { useSearchParams } from 'next/navigation';

const GoalMarketing = dynamic(() => import('./GoalMarketing'), {
  ssr: false,
  loading: () => <GoalMarketingLoadingSkeleton />,
});

type State = 'select_goal' | 'book_demo';

const steps = [
  {
    name: 'select_goal',
    title: 'Select Goal',
  },
  {
    name: 'book_demo',
    title: 'Book Demo',
  },
];

const BookDemoModal = ({
  open,
  onClose,
  activeState,
  activeGoal = undefined,
  goals = [],
  isOtplessEnabled = false,
  appRedirectionScreen = APP_REDIRECTION_SCREENS.PAYMENT,
  shouldTriggerTruecaller,
  pageSource,
}: {
  open: boolean;
  onClose: () => void;
  activeState: State;
  activeGoal?: GoalProps;
  goals?: GoalProps[];
  isOtplessEnabled?: boolean;
  appRedirectionScreen?: APP_REDIRECTION_SCREENS;
  shouldTriggerTruecaller?: boolean;
  pageSource?: string;
}) => {
  const { isMobile } = useIsMobile();
  const [isClosing, setIsClosing] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const transitionDuration = 300;
  const threshold = 100;
  const startY = useRef(0);
  const params = useSearchParams();
  const otplessSigninRef = useRef<any>(null);

  const [activeInd, setActiveInd] = useState<number>(() =>
    steps.findIndex(step => step.name === activeState)
  );
  const [selectedGoal, setSelectedGoal] = useState<GoalProps | undefined>(activeGoal || goals?.[0]);
  const [cohorts, setCohorts] = useState<CohortModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string>('');
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const { deviceManufacturer } = useDeviceDetails();
  const deviceDetailsRef = useRef({ deviceManufacturer });

  const firebaseRemoteConfig = useFirebaseRemoteConfig();

  const firebaseOtpLessConfig = firebaseRemoteConfig?.otp_less_config;

  const handleGoalSelection = (goal: GoalProps) => {
    setSelectedGoal(goal);
  };

  useEffect(() => {
    deviceDetailsRef.current = { deviceManufacturer };
  }, [deviceManufacturer]);

  useEffect(() => {
    const token = params?.get('referralToken');
    if (!token) return;

    const fetchReferralToken = async () => {
      try {
        const response = await verifyReferralToken();
        if (response?.referralCode) {
          setReferralCode(response.referralCode);
        }
      } catch (error) {
        console.error('Error verifying referral token:', error);
      }
    };

    fetchReferralToken();
  }, [params]);

  const initiateTruecallerAuth = async () => {
    let otplessTruecallerRes: any = TRUECALLER_RES[400];
    const getIsIOS = (): boolean => {
      const { userAgent, platform } = window.navigator;
      // Use array destructuring for platform and userAgent
      const [ua, pf] = [userAgent, platform];
      return (
        ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
          pf
        ) || ua.includes('Mac')
      );
    };
    const oauth = async (provider: any) => {
      try {
        const oauthRes = await otplessSigninRef.current.initiate({
          channel: 'OAUTH',
          channelType: provider,
        });
        otplessTruecallerRes = TRUECALLER_RES[200];
      } catch (oauthErr) {
        otplessTruecallerRes = TRUECALLER_RES[400];
      }
    };
    if (!getIsIOS()) {
      await oauth('TRUE_CALLER');
    }
    return otplessTruecallerRes;
  };

  useEffect(() => {
    const getOtplessAppConfig = () => {
      const otplessAppConfigs: AppOtplessConfig[] =
        JSON.parse((firebaseOtpLessConfig as string) || '{}')?.configs || [];
      const otplessAppCurrEnvConfig = getCurrEnvOtplessConfig(otplessAppConfigs);
      return otplessAppCurrEnvConfig;
    };
    // Add the SDK script
    if (isOtplessEnabled && shouldTriggerTruecaller && !isScriptLoaded) {
      let appId = process.env.NEXT_PUBLIC_CJR_OTPLESS_APP_ID || '';
      if (isFromApp()) {
        const appFirebaseConfig = getOtplessAppConfig();
        appId = appFirebaseConfig?.appId || '';
      }

      const script = document.createElement('script');
      script.id = 'otpless-sdk';
      script.src = 'https://otpless.com/v4.2/headless.js';
      script.setAttribute('data-appid', appId);
      script.async = true;

      // Append the script to the head
      document.head.appendChild(script);

      const otplessCallback = (eventCallback: any) => {
        // Handle the response from OTPless
        const ONETAP = () => {
          const { response } = eventCallback;
          const { token, identities } = response;
          const identityValue = identities[0].identityValue;
          onOneTapOtpless({
            otplessToken: token,
            mobileNumber: identityValue,
            deviceManufacturer: deviceDetailsRef.current.deviceManufacturer,
          });
        };

        const FAILED = () => {
          const { response } = eventCallback;
          console.error('Authentication failed:', response);
          setLoading(false);
        };

        const OTP_AUTO_READ = () => {
          const {
            response: { otp },
          } = eventCallback;

          // Auto-submit the OTP if needed
          if (otp && otp.length >= 6) {
            // verifyOTP(otp)
            const customEvent = new CustomEvent('otpless:otp-auto-read', {
              detail: {
                otp,
              },
            });
            document.dispatchEvent(customEvent);
          }
        };

        const EVENTS_MAP: any = {
          ONETAP,
          FAILED,
          OTP_AUTO_READ,
        };

        if ('responseType' in eventCallback && EVENTS_MAP[eventCallback.responseType]) {
          EVENTS_MAP[eventCallback.responseType]();
        }
      };

      // Initialize after script loads
      script.onload = () => {
        if ((window as any).OTPless) {
          otplessSigninRef.current = new (window as any).OTPless((response: any) => {
            otplessCallback(response);
          });
        }
        setIsScriptLoaded(true);
      };
    }

    return () => {};
  }, [isOtplessEnabled, shouldTriggerTruecaller, firebaseOtpLessConfig]);

  useEffect(() => {
    const nativeBridge = (window as any)?.JSBridge;

    if (nativeBridge) {
      nativeBridge.onTruecallerAuthSuccess = (response: string) => {
        if (response) {
          const { success, data } = JSON.parse(response);
          if (success) {
            const { phoneNumber, otplessToken } = data;
            onOneTapOtpless({
              otplessToken,
              mobileNumber: phoneNumber,
              deviceManufacturer: deviceDetailsRef.current.deviceManufacturer,
            });
          }
        }
      };
    }
  }, []);

  const handleNext = () => {
    const referralUser = localStorage.getItem('referralUser') || '';

    if (isOtplessEnabled && shouldTriggerTruecaller) {
      if (isFromApp()) {
        const isTruecallerInstalled = params?.get('truecaller_installed') === 'true';
        if (isTruecallerInstalled) {
          // truecaller initialisation from the app only, if app is installed
          invokeAppMethod(APP_METHODS.INITIATE_OTPLESS_TRUECALLER);
        }
      } else {
        // always initialize for web
        initiateTruecallerAuth();
      }
    }

    eventTracker.cohortChange({
      came_from: 'landing_page',
      prev_cohort: '',
      new_cohort: selectedGoal?.cohortId as string,
      page: 'landing',
      referrer_code: referralCode,
      referrer_name: referralUser,
    });
    if (selectedGoal?.cohortId) {
      setActiveInd(prev => prev + 1);
    }
  };

  const handleUpdateInd = (num: number) => {
    if (num < activeInd) {
      setActiveInd(num);
    }
  };

  const handleBack = () => {
    setActiveInd(prev => prev - 1);
  };

  const isGoalAlreadySelected = !!activeGoal;

  const handleClose = () => {
    if (!isMobile) {
      onClose();
      return;
    }
    setIsClosing(true);
    setTimeout(() => {
      setDragOffset(0);
      eventTracker.close_popup();
      onClose();
      setIsClosing(false);
    }, transitionDuration);
  };

  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    startY.current = 'touches' in e ? e.touches[0].clientY : e.clientY;
  };

  const handleDragMove = (e: React.TouchEvent | React.MouseEvent) => {
    const currentY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const diff = currentY - startY.current;
    if (diff > 0) setDragOffset(diff);
  };

  const handleDragEnd = () => {
    if (dragOffset > threshold) {
      handleClose();
    } else {
      setDragOffset(0);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getAllCohorts();
        setCohorts(res?.data as CohortModel[]);
      } catch (error) {
        console.error({ error });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const selectedGoalCohort = useMemo(() => {
    return cohorts.find(cohort => cohort._id === selectedGoal?.cohortId);
  }, [cohorts, selectedGoal?.cohortId]);

  return (
    <OTPLessProvider>
      <Dialog className="relative z-[9999]" open={open} onClose={handleClose}>
        <div
          className="fixed inset-0"
          aria-hidden="true"
          style={{ background: 'rgba(0,0,0,0.6)' }}
        />
        <div className="fixed md:inset-0 inset-x-0 bottom-0 flex items-center justify-center">
          <Dialog.Panel
            className={cn(
              'mx-auto lg:w-[920px] md:min-h-[562px] w-screen overflow-hidden divide-y rounded-b-none rounded-t-2xl md:rounded-3xl bg-white transition-transform',
              !isClosing && isMobile && 'slideUpDrawer',
              isClosing && isMobile && 'slideDownDrawer',
              activeInd === 0 && 'p-4 lg:p-[40px]'
            )}
            style={{
              transform: isClosing ? 'translateY(100%)' : `translateY(${dragOffset}px)`,
              transition: isClosing ? 'transform 0.3s ease-out' : 'none',
            }}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="flex flex-row">
              <div
                className={cn(
                  'flex flex-col gap-4 md:gap-6 text-greyPallete-900 w-full',
                  activeInd === 1 && 'md:h-[562px] md:border-r md:border-[#D9DCE1]'
                )}
              >
                <div
                  className={cn(
                    'flex justify-center items-center md:hidden',
                    activeInd !== 0 && 'pt-4'
                  )}
                >
                  <div className="w-11 h-1 rounded bg-greyPallete-100" />
                </div>
                <XMarkIcon
                  onClick={handleClose}
                  className="absolute md:top-5 md:right-6 top-6 right-6 w-5 h-5 cursor-pointer hidden md:block"
                />
                <ChevronLeftIcon
                  onClick={handleBack}
                  width={24}
                  height={24}
                  className={cn(
                    'absolute md:top-10 md:left-7 top-4 left-6 w-5 h-5 md:w-6 md:h-6 cursor-pointer md:block z-[9999]',
                    activeInd === 0 && 'hidden md:hidden',
                    isGoalAlreadySelected && 'hidden md:block'
                  )}
                />
                {!(
                  selectedGoal?.showVideoBanner &&
                  selectedGoal?.videoBannerUrl &&
                  selectedGoal?.mwebvideoBannerUrl &&
                  activeInd === 1
                ) && (
                  <div
                    className={cn(
                      activeInd === 1 && 'pt-4 md:pt-10 md:pl-10',
                      isGoalAlreadySelected && 'hidden md:block'
                    )}
                  >
                    <Stepper
                      activeInd={activeInd}
                      steps={steps}
                      handleUpdateInd={handleUpdateInd}
                    />
                  </div>
                )}

                {activeInd === 0 && (
                  <SelectGoalStep
                    handleNext={handleNext}
                    selectedGoal={selectedGoal as GoalProps}
                    handleGoalSelection={handleGoalSelection}
                    goals={goals as GoalProps[]}
                  />
                )}

                {activeInd === 1 && <BookDemoStep goal={selectedGoal as GoalProps} />}

                {loading && isMobile ? (
                  <div className="h-40 w-full flex items-center justify-center">
                    <Loader color="#ff6d0a" />
                  </div>
                ) : null}

                {activeInd === 1 && isMobile && (
                  <div>
                    <GoalMarketing
                      goal={selectedGoal as GoalProps}
                      isBannerVisible={isGoalAlreadySelected}
                      goalCohort={selectedGoalCohort as CohortModel}
                      isOtplessEnabled={isOtplessEnabled}
                      appRedirectionScreen={appRedirectionScreen}
                      shouldTriggerTruecaller={shouldTriggerTruecaller || false}
                      pageSource={pageSource}
                    />
                  </div>
                )}
              </div>

              {activeInd === 1 && !isMobile && (
                <div className="hidden md:block md:min-w-[360px] my-4 mx-2">
                  <GoalMarketing
                    goal={selectedGoal as GoalProps}
                    isBannerVisible={isGoalAlreadySelected}
                    goalCohort={selectedGoalCohort as CohortModel}
                    isOtplessEnabled={isOtplessEnabled}
                    appRedirectionScreen={appRedirectionScreen}
                    shouldTriggerTruecaller={shouldTriggerTruecaller || false}
                    pageSource={pageSource}
                  />
                </div>
              )}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </OTPLessProvider>
  );
};

export default BookDemoModal;
